import * as React from "react";
import { createPortal } from "react-dom";
import { Plus, FileText, X } from "lucide-react";
import type { CertificationTraining } from "../../lib/api/certifications";
import { useCertificationContentDefinitions } from "../../lib/i18n/useCertificationContentDefinitions";
import { isContentLocKey } from "../../lib/i18n/contentKeys";
import { isYoutubeUrl } from "../../lib/media/youtube";
import { YoutubeIcon } from "../../../ui/components/Icon";
import { Button } from "../../../ui/components/Button";
import { useI18n } from "../../lib/i18n";
import { trainingPoster } from "./trainingPoster";
import * as styles from "./ModuleDetailPage.css";

const CLICK_THRESHOLD_PX = 6;
const ENTER_MS = 300;

function sortTrainings(trainings: CertificationTraining[]): CertificationTraining[] {
  return [...trainings].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber,
  );
}

type DragGhost = {
  posterUrl: string | null;
  videoUrl: string | null;
  title: string;
  step: number;
  w: number;
  h: number;
};

export function CourseTrainingsStrip({
  trainings,
  selectedId,
  onSelect,
  onReorder,
  onAdd,
  onDelete,
  unsavedCounts,
  reordering = false,
}: {
  trainings: CertificationTraining[];
  selectedId: string | null;
  onSelect: (training: CertificationTraining) => void;
  onReorder: (ordered: CertificationTraining[]) => void;
  onAdd: () => void;
  onDelete: (training: CertificationTraining) => void;
  unsavedCounts?: Record<string, number>;
  reordering?: boolean;
}) {
  const { t } = useI18n();
  const { resolve } = useCertificationContentDefinitions();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = React.useState(false);
  const [showRightFade, setShowRightFade] = React.useState(false);

  const sorted = React.useMemo(() => sortTrainings(trainings), [trainings]);
  const sortedRef = React.useRef(sorted);
  sortedRef.current = sorted;

  const [dragSrcIdx, setDragSrcIdx] = React.useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);
  const dragOverIdxRef = React.useRef<number | null>(null);
  dragOverIdxRef.current = dragOverIdx;

  const [dragGhost, setDragGhost] = React.useState<DragGhost | null>(null);
  const [dragGhostPos, setDragGhostPos] = React.useState({ x: 0, y: 0 });
  const dragGrabOffset = React.useRef({ x: 0, y: 0 });
  const pointerStart = React.useRef<{ x: number; y: number } | null>(null);
  const didDragRef = React.useRef(false);
  /** True while pointer is down — drag visuals only activate after move threshold. */
  const [pointerTracking, setPointerTracking] = React.useState(false);
  const pendingDragRef = React.useRef<{
    idx: number;
    ghost: DragGhost;
  } | null>(null);

  const [enteringIds, setEnteringIds] = React.useState<Set<string>>(new Set());

  const updateFades = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftFade(scrollLeft > 4);
    setShowRightFade(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  React.useLayoutEffect(() => {
    updateFades();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateFades, sorted.length]);

  React.useEffect(() => {
    if (!pointerTracking) return;

    const onPointerMove = (e: PointerEvent) => {
      const start = pointerStart.current;
      const pending = pendingDragRef.current;

      if (start && pending && !didDragRef.current) {
        const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y);
        if (dist < CLICK_THRESHOLD_PX) return;

        didDragRef.current = true;
        document.documentElement.style.setProperty("cursor", "grabbing", "important");
        setDragSrcIdx(pending.idx);
        setDragGhost(pending.ghost);
        setDragGhostPos({ x: e.clientX, y: e.clientY });
      }

      if (!didDragRef.current) return;

      setDragGhostPos({ x: e.clientX, y: e.clientY });
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const tileEl = el?.closest("[data-drag-idx]") as HTMLElement | null;
      const over = tileEl ? parseInt(tileEl.dataset.dragIdx ?? "-1", 10) : -1;
      setDragOverIdx(over >= 0 ? over : null);
    };

    const onPointerUp = () => {
      document.documentElement.style.removeProperty("cursor");
      const src = pendingDragRef.current?.idx ?? null;
      const over = dragOverIdxRef.current;
      const moved = didDragRef.current;

      setPointerTracking(false);
      pendingDragRef.current = null;
      setDragSrcIdx(null);
      setDragOverIdx(null);
      setDragGhost(null);
      pointerStart.current = null;
      didDragRef.current = false;

      if (moved && src !== null && over !== null && src !== over) {
        const next = [...sortedRef.current];
        const [item] = next.splice(src, 1);
        next.splice(over, 0, item);
        onReorder(next);
        requestAnimationFrame(() => {
          const lo = Math.min(src, over);
          const hi = Math.max(src, over);
          const movedIds = next.slice(lo, hi + 1).map((i) => i.id);
          setEnteringIds((p) => {
            const set = new Set(p);
            movedIds.forEach((id) => set.add(id));
            return set;
          });
          window.setTimeout(() => {
            setEnteringIds((p) => {
              const set = new Set(p);
              movedIds.forEach((id) => set.delete(id));
              return set;
            });
          }, ENTER_MS);
        });
      } else if (!moved && src !== null) {
        const training = sortedRef.current[src];
        if (training) onSelect(training);
      }
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [pointerTracking, onReorder, onSelect]);

  const handleTilePointerDown = (idx: number) => (e: React.PointerEvent) => {
    if (reordering || e.button !== 0) return;
    e.preventDefault();

    const training = sorted[idx];
    if (!training) return;

    const el = e.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);

    dragGrabOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    pointerStart.current = { x: e.clientX, y: e.clientY };
    didDragRef.current = false;

    const title = isContentLocKey(training.title)
      ? resolve(training.title) || "…"
      : training.title;

    pendingDragRef.current = {
      idx,
      ghost: {
        posterUrl: trainingPoster(training, idx),
        videoUrl:
          !training.thumbnailUrl && training.videoUrl && !isYoutubeUrl(training.videoUrl)
            ? training.videoUrl
            : null,
        title,
        step: idx + 1,
        w,
        h,
      },
    };
    setPointerTracking(true);
  };

  const isDragActive = dragSrcIdx !== null;

  return (
    <div className={styles.trainingsBlock}>
      <div className={styles.trainingsHeader}>
        <p className={styles.fieldLabel}>{t("pro.system.modules.course.trainingsLabel")}</p>
        <Button
          type="button"
          color="primary"
          variant="outline"
          size="sm"
          iconLeft={<Plus size={14} />}
          onClick={onAdd}
          disabled={reordering || isDragActive}
        >
          {t("pro.system.modules.trainings.add")}
        </Button>
      </div>
      <div className={styles.trainingsGalleryWrap}>
        <div
          ref={scrollRef}
          className={styles.trainingsGalleryScroll}
          onScroll={updateFades}
        >
          <div
            className={[
              styles.trainingsRow,
              isDragActive ? styles.trainingsRowDragging : "",
            ].filter(Boolean).join(" ")}
          >
            {sorted.map((training, index) => {
              const title = isContentLocKey(training.title)
                ? resolve(training.title) || "…"
                : training.title;
              const poster = trainingPoster(training, index);
              const youtube = isYoutubeUrl(training.videoUrl);
              const step = index + 1;
              const selected = selectedId === training.id;
              const isDragSrc = dragSrcIdx === index;
              const isDropTarget =
                dragOverIdx === index && dragSrcIdx !== null && dragSrcIdx !== index;
              const isSrcHover =
                dragOverIdx === index && dragSrcIdx === index;
              const isEntering = enteringIds.has(training.id);

              const unsavedCount = unsavedCounts?.[training.id] ?? 0;

              return (
                <div
                  key={training.id}
                  role="button"
                  tabIndex={0}
                  data-drag-idx={index}
                  className={[
                    styles.trainingTile,
                    selected ? styles.trainingTileSelected : "",
                    isDragSrc ? styles.trainingTileDragging : "",
                    isSrcHover ? styles.trainingTileDragSrcHover : "",
                    isDropTarget ? styles.trainingTileDropTarget : "",
                    isEntering ? styles.trainingTileEnter : "",
                  ].filter(Boolean).join(" ")}
                  onPointerDown={handleTilePointerDown(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(training);
                    }
                  }}
                  title={title}
                  aria-pressed={selected}
                  aria-disabled={reordering || undefined}
                >
                  <div className={styles.trainingTileMedia}>
                    {poster ? (
                      <img src={poster} alt="" className={styles.trainingTileImg} />
                    ) : training.videoUrl && !youtube ? (
                      <video
                        src={training.videoUrl}
                        className={styles.trainingTileImg}
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <div className={styles.trainingTilePlaceholder}>
                        <FileText size={28} aria-hidden />
                      </div>
                    )}
                    {youtube ? (
                      <span className={styles.trainingTileYoutube} aria-hidden>
                        <YoutubeIcon size={12} />
                      </span>
                    ) : null}
                    {unsavedCount > 0 && !isDragSrc ? (
                      <span
                        className={styles.trainingTileUnsaved}
                        title={
                          unsavedCount === 1
                            ? "1 unsaved change"
                            : `${unsavedCount} unsaved changes`
                        }
                      >
                        {unsavedCount === 1
                          ? "1 unsaved change"
                          : `${unsavedCount} unsaved changes`}
                      </span>
                    ) : null}
                    <span className={styles.trainingTileStep}>{step}</span>
                    {!isDragSrc ? (
                      <button
                        type="button"
                        className={styles.trainingTileDelete}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onDelete(training);
                        }}
                        aria-label={t("shared.common.delete")}
                        title={t("shared.common.delete")}
                        disabled={reordering}
                      >
                        <X size={10} strokeWidth={3} aria-hidden />
                      </button>
                    ) : null}
                  </div>
                  <span className={styles.trainingTileTitle}>{title}</span>
                  {selected && !isDragSrc ? (
                    <span className={styles.trainingTileCaret} aria-hidden />
                  ) : null}
                </div>
              );
            })}

            <button
              type="button"
              className={styles.trainingTileAdd}
              onClick={onAdd}
              aria-label={t("pro.system.modules.trainings.add")}
              title={t("pro.system.modules.trainings.add")}
              disabled={reordering || isDragActive}
            >
              <Plus size={22} aria-hidden />
              <span>{t("pro.system.modules.trainings.add")}</span>
            </button>
          </div>
        </div>
        <div
          className={styles.trainingsFadeLeft}
          style={{ opacity: showLeftFade ? 1 : 0 }}
          aria-hidden
        />
        <div
          className={styles.trainingsFadeRight}
          style={{ opacity: showRightFade ? 1 : 0 }}
          aria-hidden
        />
      </div>

      {dragGhost
        ? createPortal(
            <div
              className={styles.trainingDragGhost}
              style={{
                left: dragGhostPos.x - dragGrabOffset.current.x,
                top: dragGhostPos.y - dragGrabOffset.current.y,
                width: dragGhost.w,
                height: dragGhost.h,
                transformOrigin: `${dragGrabOffset.current.x}px ${dragGrabOffset.current.y}px`,
              }}
            >
              <div className={styles.trainingTileMedia}>
                {dragGhost.posterUrl ? (
                  <img src={dragGhost.posterUrl} alt="" className={styles.trainingTileImg} />
                ) : dragGhost.videoUrl ? (
                  <video
                    src={dragGhost.videoUrl}
                    className={styles.trainingTileImg}
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <div className={styles.trainingTilePlaceholder}>
                    <FileText size={28} aria-hidden />
                  </div>
                )}
                <span className={styles.trainingTileStep}>{dragGhost.step}</span>
              </div>
              <span className={styles.trainingTileTitle}>{dragGhost.title}</span>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
